import type { GatewayOwnership, GatewayStatus, InstallationStatus } from "@/contracts";

import type { Tone } from "@/components/ui/badge";
import i18n from "@/i18n";

export const GATEWAY_STATUS_LABELS: Record<GatewayStatus, string> = {
  get ONLINE() {
    return i18n.t("pages:gatewayStatus.ONLINE");
  },
  get OFFLINE() {
    return i18n.t("pages:gatewayStatus.OFFLINE");
  },
  get UNKNOWN() {
    return i18n.t("pages:gatewayStatus.UNKNOWN");
  },
};

/**
 * Who paid for the hardware, which is a commercial question rather than a
 * technical one: a gateway covers many sensors, so whoever owns it is the
 * party the site's coverage depends on.
 */
export const GATEWAY_OWNERSHIP_LABELS: Record<GatewayOwnership, string> = {
  get PLATFORM() {
    return i18n.t("pages:gatewayOwnership.PLATFORM");
  },
  get CUSTOMER() {
    return i18n.t("pages:gatewayOwnership.CUSTOMER");
  },
  get THIRD_PARTY() {
    return i18n.t("pages:gatewayOwnership.THIRD_PARTY");
  },
  get UNKNOWN() {
    return i18n.t("pages:gatewayOwnership.UNKNOWN");
  },
};

/**
 * The installation procedure as a person would describe it.
 *
 * "Waiting for the first reading" is deliberately phrased as something being
 * waited for rather than a state somebody forgot to complete: nobody can
 * complete it. The device has to be heard (ADR-062).
 */
export const INSTALLATION_STATUS_LABELS: Record<InstallationStatus, string> = {
  get DRAFT() {
    return i18n.t("pages:installationStatus.DRAFT");
  },
  get AWAITING_UPLINK() {
    return i18n.t("pages:installationStatus.AWAITING_UPLINK");
  },
  get COMMISSIONED() {
    return i18n.t("pages:installationStatus.COMMISSIONED");
  },
  get FAILED() {
    return i18n.t("pages:installationStatus.FAILED");
  },
};

/** Colour follows meaning: only one of these means somebody can stop worrying. */
export const INSTALLATION_STATUS_TONES: Record<InstallationStatus, Tone> = {
  DRAFT: "neutral",
  AWAITING_UPLINK: "warn",
  COMMISSIONED: "ok",
  FAILED: "danger",
};

export const GATEWAY_STATUS_TONES: Record<GatewayStatus, Tone> = {
  ONLINE: "ok",
  OFFLINE: "danger",
  UNKNOWN: "neutral",
};

/**
 * How a radio signal reads to somebody who is not a radio engineer.
 *
 * The boundaries are the conventional LoRaWAN rules of thumb, not something
 * measured for this hardware in these buildings — **to verify** against real
 * installations before anyone treats a label here as a guarantee. They are
 * offered as a hint and the raw dBm is always shown beside them, because the
 * number is the fact and the word is an opinion.
 */
export function signalHint(rssi: number | null): { label: string; tone: Tone } | null {
  if (rssi === null) {
    return null;
  }

  if (rssi >= -85) {
    return { label: i18n.t("pages:signal.comfortable"), tone: "ok" };
  }

  if (rssi >= -105) {
    return { label: i18n.t("pages:signal.workable"), tone: "warn" };
  }

  return { label: i18n.t("pages:signal.marginal"), tone: "danger" };
}
